import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { DateConstants, IconDefinitionConstants } from "utils/constants";
import { MonthSelectorProps } from "models/propTypes/timeReportingPropTypes";
import { DialogEnums } from "models/enums";
//#endregion
//#region components
import { ExtendedEntityPagination } from "components/common";
import { Grid } from "@mui/material";
import PeriodToggle from "../common/periodToggle";
import usePeriodSwitch from "../common/usePeriodSwitch";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const MonthSelector: React.FC<MonthSelectorProps> = (props) => {
  const { onMonthToggle, currentMonthDateRange, hasUnSavedChanges, isSaving } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  // Leaving the month, by its arrows or for the week view, drops edits that are not saved, so it asks first.
  const leaveMonth = (leave: () => void, discardMessage: string) => {
    if (isSaving) {
      return;
    }
    if (!hasUnSavedChanges?.()) {
      leave();
      return;
    }
    dispatch(
      displayDialog({
        isOpen: false,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.UnSavedChanges),
        titleIcon: IconDefinitionConstants.WQuestionCircle,
        dialogType: DialogEnums.DialogType.Message,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DontSave),
            onClick: async () => leave(),
          },
        ],
        children: LocalizationHelperInstance.getFormattedMessage(discardMessage),
      })
    );
  };

  const toggleMonth = (newLoadingDate: Date) =>
    leaveMonth(() => onMonthToggle(newLoadingDate), TranslationKeys.TimeReporting.DiscardMonthChanges);

  const onNextMonthClick = () => {
    const newLoadingDate = currentMonthDateRange?.end && DateHelper.getFirstDayOfNextMonth(currentMonthDateRange.end);
    newLoadingDate && toggleMonth(newLoadingDate);
  };

  const onPreviousMonthClick = () => {
    const newLoadingDate =
      currentMonthDateRange?.end && DateHelper.getFirstDayOfPreviousMonth(currentMonthDateRange.end);
    newLoadingDate && toggleMonth(newLoadingDate);
  };

  const { showWeek } = usePeriodSwitch();

  const onShowWeekClick = () => {
    const month = currentMonthDateRange;
    month && leaveMonth(() => showWeek(month), TranslationKeys.TimeReporting.DiscardMonthChangesForWeek);
  };

  return (
    <ExtendedEntityPagination
      textNext={TranslationKeys.TimeReporting.NextMonth}
      textPrevious={TranslationKeys.TimeReporting.PreviousMonth}
      onNavigationNext={onNextMonthClick}
      onNavigationPrevious={onPreviousMonthClick}
      actions={<PeriodToggle period="month" onShowWeek={onShowWeekClick} />}
    >
      <Grid container direction="column">
        <Grid item>
          {props.currentMonthDateRange?.start &&
            LocalizationHelperInstance.getFormattedDate(
              props.currentMonthDateRange?.start,
              DateConstants.MonthLongConfig
            )}
        </Grid>
        <Grid item className={cssExports.titleCaption}>
          {currentMonthDateRange?.start &&
            currentMonthDateRange?.end &&
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekNumberRange, "", {
              firstWeekNumber: DateHelper.getWeekNumber(currentMonthDateRange.start),
              lastWeekNumber: DateHelper.getWeekNumber(currentMonthDateRange.end),
            })}
        </Grid>
      </Grid>
    </ExtendedEntityPagination>
  );
};

export default MonthSelector;
